import React, { useState, useRef, useEffect, useId } from 'react';
import { MagnifyingGlassIcon, ChevronUpDownIcon, XMarkIcon, UserCircleIcon } from '@heroicons/react/24/outline';

/**
 * SearchableSelect - A filterable dropdown component with enhanced UX for large lists
 * @param {string} value - Selected value
 * @param {function} onChange - Callback when selection changes
 * @param {array} options - Array of {value, label, isActive, lastActive} objects
 * @param {string} placeholder - Placeholder text
 * @param {boolean} disabled - Whether the select is disabled
 * @param {string} className - Additional CSS classes
 * @param {boolean} showCount - Show total count of options
 * @param {boolean} enableActivityFilter - Enable activity filtering (active/inactive)
 */
export default function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  disabled = false,
  className = '',
  showCount = true,
  enableActivityFilter = false,
  leftIcon = null,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [activityFilter, setActivityFilter] = useState('active'); // 'all', 'active', 'inactive'
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const listboxId = `ss-listbox-${useId()}`;

  // Filter options based on search term and activity
  const filteredOptions = options.filter(option => {
    const matchesSearch = option.label.toLowerCase().includes(searchTerm.toLowerCase());

    if (!enableActivityFilter || activityFilter === 'all') {
      return matchesSearch;
    }

    if (activityFilter === 'active') {
      return matchesSearch && option.isActive;
    }

    if (activityFilter === 'inactive') {
      return matchesSearch && !option.isActive;
    }

    return matchesSearch;
  });

  // Get selected option label (handle both string and number values)
  const selectedOption = options.find(opt => String(opt.value) === String(value));
  const selectedLabel = selectedOption ? selectedOption.label : '';


  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Reset highlighted index when filtered options change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchTerm]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        break;
      default:
        break;
    }
  };

  // Scroll highlighted option into view
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.children[highlightedIndex];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedIndex, isOpen]);

  const handleSelect = (optionValue) => {
    if (onChange && typeof onChange === 'function') {
      onChange(optionValue);
    }
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (onChange && typeof onChange === 'function') {
      onChange('');
    }
    setSearchTerm('');
  };

  if (disabled) {
    return (
      <div className={`px-3 py-2 rounded-md border border-gray-200/70 dark:border-gray-600/70 bg-gray-100 dark:bg-gray-870 text-sm text-gray-500 dark:text-gray-400 ${className}`}>
        {selectedLabel || placeholder}
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={listboxId}
        className="w-full px-3 py-2 rounded-md border border-gray-200/70 dark:border-gray-600/70 bg-surface dark:bg-gray-870 text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500 flex items-center justify-between hover:border-gray-300 dark:hover:border-gray-600 transition-colors"
      >
        <div className="flex items-center gap-2 truncate flex-1">
          {leftIcon === 'user' && (
            <UserCircleIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
          )}
          <span className={`truncate ${!selectedLabel ? 'text-gray-400' : ''}`}>
            {selectedLabel || placeholder}
          </span>
          {showCount && options.length > 0 && (
            <span className="px-2 py-0.5 rounded-full border border-gray-300 dark:border-gray-600 text-xs text-gray-600 dark:text-gray-400 font-medium flex-shrink-0">
              {options.length}
            </span>
          )}
        </div>
        {/* Reserve space for the clear button + chevron */}
        <div className="flex items-center gap-1 ml-2 flex-shrink-0">
          {selectedLabel && <span className="w-5" aria-hidden="true" />}
          <ChevronUpDownIcon className="w-5 h-5 text-gray-400" />
        </div>
      </button>
      {selectedLabel && (
        <button
          type="button"
          onClick={handleClear}
          aria-label="Clear selection"
          className="absolute right-8 top-1/2 -translate-y-1/2 p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <XMarkIcon className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
        </button>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 rounded-lg bg-surface dark:bg-gray-870 shadow-xl border border-gray-200/70 dark:border-gray-600/70 max-h-[400px] overflow-hidden flex flex-col">
          {/* Search input with enhanced design */}
          <div className="p-3 border-b border-gray-200/70 dark:border-gray-600/70 bg-surface dark:bg-gray-870/50">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type to search..."
                aria-label="Search options"
                className="w-full pl-9 pr-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>

            {/* Activity Filter Toggles */}
            {enableActivityFilter && (
              <div className="mt-3 flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setActivityFilter('all')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    activityFilter === 'all'
                      ? 'bg-brand-600 text-white shadow-sm'
                      : 'bg-surface dark:bg-gray-870 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600'
                  }`}
                >
                  All
                </button>
                <button
                  type="button"
                  onClick={() => setActivityFilter('active')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1 ${
                    activityFilter === 'active'
                      ? 'bg-green-600 text-white shadow-sm'
                      : 'bg-surface dark:bg-gray-870 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${activityFilter === 'active' ? 'bg-white' : 'bg-green-500'}`}></span>
                  Active
                </button>
                <button
                  type="button"
                  onClick={() => setActivityFilter('inactive')}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all flex items-center gap-1 ${
                    activityFilter === 'inactive'
                      ? 'bg-amber-600 text-white shadow-sm'
                      : 'bg-surface dark:bg-gray-870 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${activityFilter === 'inactive' ? 'bg-white' : 'bg-amber-500'}`}></span>
                  Inactive
                </button>
              </div>
            )}

            {/* Results counter */}
            {(searchTerm || (enableActivityFilter && activityFilter !== 'all')) && (
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-center justify-between">
                <span>{filteredOptions.length} of {options.length} users</span>
                {searchTerm && (
                  <button
                    type="button"
                    onClick={() => setSearchTerm('')}
                    className="text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 font-medium"
                  >
                    Clear search
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Options list with enhanced design */}
          <div
            ref={dropdownRef}
            role="listbox"
            id={listboxId}
            className="overflow-y-auto flex-1"
            style={{
              maxHeight: '320px',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgb(156 163 175) transparent'
            }}
          >
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {searchTerm ? (
                    <>
                      <div className="mb-2">No users found matching "{searchTerm}"</div>
                      <button
                        type="button"
                        onClick={() => setSearchTerm('')}
                        className="text-xs text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
                      >
                        Clear search
                      </button>
                    </>
                  ) : (
                    'No users available'
                  )}
                </div>
              </div>
            ) : (
              filteredOptions.map((option, index) => {
                const isSelected = String(option.value) === String(value);
                const isHighlighted = index === highlightedIndex;

                return (
                  <div
                    key={option.value !== undefined && option.value !== null ? option.value : `option-${index}-${option.label}`}
                    role="option"
                    aria-selected={isSelected}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSelect(option.value);
                    }}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={`w-full px-4 py-2.5 text-left text-sm cursor-pointer transition-all ${
                      isSelected
                        ? 'bg-brand-800 dark:bg-brand-600 text-white font-semibold shadow-sm'
                        : isHighlighted
                        ? 'bg-brand-50 dark:bg-brand-900/20 text-gray-900 dark:text-gray-100'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        {/* text-white must sit on the span itself: index.css sets a
                            global `span`/`.dark span` color in @layer base, which an
                            explicit rule beats inherited color from the parent. The
                            dark: variant is needed to out-rank `.dark span`. */}
                        <span className={`truncate ${isSelected ? 'text-white dark:text-white' : ''}`}>{option.label}</span>
                        {option.notEvaluated && (
                          <span
                            className={`flex-shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                              isSelected
                                ? 'bg-amber-200/80 text-amber-900'
                                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                            }`}
                            title="No MarketMonthlyStats row for this period - typically incomplete D+N session coverage."
                          >
                            Not evaluated
                          </span>
                        )}
                      </div>
                      {isSelected && (
                        <svg className="w-4 h-4 flex-shrink-0 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

